import { useState } from 'react';
import ConsultationModal from './ConsultationModal';

const mainProjects = [
  {
    number: '01',
    category: 'CHEMICAL INDUSTRY',
    title: 'Industrial Wastewater Treatment Complex (ZLD)',
    solution: 'Clarification + Ultrafiltration + 3-stage Reverse Osmosis + Disc Osmosis + Evaporation + Sludge Dewatering',
    result: 'Minimization of liquid discharge and increased water recycling',
    scope: 'Concept design (OTR) development',
    accent: 'dark' as const,
  },
  {
    number: '02',
    category: 'ENVIRONMENTAL & WASTE MANAGEMENT',
    title: 'Three-stage Reverse Osmosis System',
    capacity: '350 m³/h',
    solution: 'Treatment of highly mineralized wastewater',
    result: 'Reduction of salinity and stable discharge parameters',
    scope: 'Working documentation, detailed engineering, installation & commissioning',
    accent: 'blue' as const,
  },
  {
    number: '03',
    category: 'OIL REFINING INDUSTRY',
    title: 'Oxidation Reactor Modules',
    solution: 'Manufacturing and supply of technological equipment',
    result: 'Integration into industrial process streams with increased operational reliability',
    scope: 'Equipment production and delivery',
    accent: 'light' as const,
  },
];

const additionalProjects = [
  {
    industry: 'Petrochemical Industry',
    title: 'Reagent storage, transportation and preparation units',
    scope: 'Supply + detailed engineering + installation & commissioning',
    icon: 'ri-flask-line',
  },
  {
    industry: 'Municipal Solid Waste Infrastructure',
    title: 'Landfill wastewater treatment facilities',
    scope: 'Working documentation development',
    icon: 'ri-recycle-line',
  },
  {
    industry: 'Mining / Resource Extraction',
    title: 'Mechanical filtration + Reverse Osmosis',
    scope: 'Process and automation design documentation',
    icon: 'ri-hammer-line',
  },
  {
    industry: 'Food & Beverage Industry',
    title: 'Sanitary Reverse Osmosis System',
    scope: 'Supply + installation + commissioning + training',
    icon: 'ri-cup-line',
  },
  {
    industry: 'Industrial Manufacturing',
    title: 'Reverse Osmosis automation',
    scope: 'SCADA & MES integration, real-time monitoring',
    icon: 'ri-settings-3-line',
  },
];

function MainProjectCard({ project, onConsult }: { project: typeof mainProjects[0]; onConsult: () => void }) {
  const isDark = project.accent === 'dark';
  const isBlue = project.accent === 'blue';

  const bg = isDark
    ? 'bg-[#0A1628]'
    : isBlue
    ? 'bg-gradient-to-br from-[#0066FF] to-[#0099FF]'
    : 'bg-white border border-[#E8F0FF]';

  const numColor = isDark ? 'text-white/5' : isBlue ? 'text-white/10' : 'text-[#0A1628]/5';
  const catColor = isDark ? 'text-[#0066FF]' : isBlue ? 'text-white/70' : 'text-[#0066FF]';
  const titleColor = isDark ? 'text-white' : isBlue ? 'text-white' : 'text-[#0A1628]';
  const labelColor = isDark ? 'text-white/40' : isBlue ? 'text-white/60' : 'text-[#0A1628]/50';
  const valueColor = isDark ? 'text-white/90' : isBlue ? 'text-white' : 'text-[#0A1628]';
  const dividerColor = isDark ? 'border-white/10' : isBlue ? 'border-white/20' : 'border-[#E8F0FF]';
  const tagBg = isDark ? 'bg-white/5 border-white/10 text-white/70' : isBlue ? 'bg-white/15 border-white/20 text-white' : 'bg-[#F7FAFF] border-[#E8F0FF] text-[#0A1628]';
  const btnColor = isDark ? 'text-[#0066FF] hover:text-[#3385FF]' : isBlue ? 'text-white hover:text-white/80' : 'text-[#0066FF] hover:text-[#0052CC]';

  return (
    <div className={`rounded-[24px] p-8 flex flex-col gap-6 relative overflow-hidden ${bg}`}>
      {/* Big number bg */}
      <span className={`absolute top-4 right-6 text-[140px] font-black leading-none select-none pointer-events-none ${numColor}`}>
        {project.number}
      </span>

      {/* Top */}
      <div className="relative z-10">
        <span className={`text-[9px] uppercase tracking-[3px] font-bold ${catColor}`}>{project.category}</span>
        <h3 className={`text-xl font-black uppercase tracking-tight mt-2 leading-snug ${titleColor}`}>{project.title}</h3>
      </div>

      {/* Capacity badge if exists */}
      {'capacity' in project && project.capacity && (
        <div className={`relative z-10 self-start px-4 py-2 rounded-full border text-sm font-bold ${tagBg}`}>
          <span className={`text-[9px] uppercase tracking-[2px] mr-2 ${labelColor}`}>Capacity</span>
          {project.capacity}
        </div>
      )}

      {/* Fields */}
      <div className={`relative z-10 flex flex-col gap-4 border-t pt-5 ${dividerColor}`}>
        <div>
          <div className={`text-[9px] uppercase tracking-[2px] font-bold mb-1 ${labelColor}`}>Solution</div>
          <div className={`text-sm font-medium leading-relaxed ${valueColor}`}>{project.solution}</div>
        </div>
        <div className={`border-t ${dividerColor}`}></div>
        <div>
          <div className={`text-[9px] uppercase tracking-[2px] font-bold mb-1 ${labelColor}`}>Result</div>
          <div className={`text-sm font-medium leading-relaxed ${valueColor}`}>{project.result}</div>
        </div>
        <div className={`border-t ${dividerColor}`}></div>
        <div>
          <div className={`text-[9px] uppercase tracking-[2px] font-bold mb-1 ${labelColor}`}>Scope</div>
          <div className={`text-sm font-medium leading-relaxed ${valueColor}`}>{project.scope}</div>
        </div>
      </div>
    </div>
  );
}

export default function Projects() {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <section id="projects" className="bg-gradient-to-br from-[#F8FBFF] via-white to-[#EEF5FF] py-16 lg:py-24">
      <div className="max-w-[1400px] mx-auto px-4 sm:px-6 lg:px-8">

        {/* Header */}
        <div className="mb-10 lg:mb-16">
          <h2 className="text-[32px] sm:text-[44px] lg:text-[64px] font-black text-[#0A1628] uppercase leading-none tracking-tight mb-3">
            IMPLEMENTED<br />SOLUTIONS
          </h2>
          <div className="w-16 h-1 bg-[#0066FF]"></div>
        </div>

        {/* Main 3 cards */}
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 lg:gap-6 mb-10 lg:mb-16">
          {mainProjects.map((project) => (
            <MainProjectCard key={project.number} project={project} onConsult={() => setIsModalOpen(true)} />
          ))}
        </div>

        {/* Additional projects */}
        <div className="border-t border-[#E8F0FF] pt-8 lg:pt-12">
          <div className="flex items-center gap-3 mb-6 lg:mb-8">
            <div className="w-1 h-6 bg-[#0066FF] rounded-full"></div>
            <span className="text-[11px] uppercase tracking-[3px] font-bold text-[#0A1628]/50">Additional Completed Projects</span>
          </div>

          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-5 gap-3 lg:gap-4">
            {additionalProjects.map((p, i) => (
              <div
                key={i}
                className="rounded-[18px] bg-[#F7FAFF] border border-[#E8F0FF] p-4 lg:p-5 flex flex-col gap-3"
              >
                <div className="w-9 h-9 flex items-center justify-center rounded-xl bg-white border border-[#E8F0FF]">
                  <i className={`${p.icon} text-[#0066FF] text-base`}></i>
                </div>
                <div>
                  <div className="text-[8px] uppercase tracking-[2px] font-bold text-[#0066FF] mb-1 leading-tight">{p.industry}</div>
                  <div className="text-sm font-bold text-[#0A1628] leading-snug mb-2">{p.title}</div>
                  <div className="text-[11px] text-[#0A1628]/50 leading-relaxed">{p.scope}</div>
                </div>
              </div>
            ))}
          </div>
        </div>

      </div>

      <ConsultationModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </section>
  );
}
